// Atlas — Solar System Browser, "Tap, Click, Click, Mars"
// Vertical slice of the canonical Atlas spec (atlas-00-architecture.md +
// 03-land/*).
//
// There is no tier ladder any more, only an ALTITUDE. One focus-point camera
// (src/atlas/camera.ts) flies from 126 AU to an eye height of a metre and a
// half, the band it is in is a pure function of that altitude, and the eight
// rung labels the HUD prints are a second pure function of it:
//
//   SYSTEM   heliocentric — the registry's bodies at real radii and distances
//   ORBIT    the body's whole disc, limb to limb
//   REGIONAL a quadrangle: the MC-30 outlines
//   LOCAL    the survey hexes, in context and then close in
//   CLAIM    the site's baked height field
//   PLOT     one plot of it at four times the detail
//   SURFACE  boots, rocks, the rover
//
// Crossing an edge changes a label, the inspector's density and what the
// streamer is asked for. It never loads, unloads, rebuilds, fades or scales
// anything, which is the whole of docs/roadmap/02-continuous-descent.md.
//
// Verb: SCROLL to descend anywhere, drag to orbit anywhere, double-click to aim.
//
// Explicitly DEFERRED (drawn line):
//   - No ownership / NFT / wallet / claim verb (Phase 4)
//   - The claim and below still live in the plot's own local frame at the scene
//     origin; Phase 2 makes them the deepest levels of the body's own quadtree

import * as THREE from 'three';
import { DtmPlot } from './atlas/dtmPlot.ts';
import { TEST_SWITCHES, urlFlag } from './urlFlags.ts';
import { ensureAddresses } from './atlas/plotName.ts';
import { type PlotField, type SubPlot } from './atlas/plotField.ts';
import { createInspector } from './atlas/inspector.ts';
import { createEventLog } from './atlas/panels/eventLog.ts';
import { _sunFixed, createSolarScrub } from './atlas/solarScrub.ts';
import { createPick } from './atlas/pick.ts';
import { createPanels } from './atlas/panels.ts';
import { createInput } from './atlas/input.ts';
import { createInjects } from './atlas/injects.ts';
import { createCascades } from './atlas/cascades.ts';
import { overlayMarkup, injectPlotStyles } from './atlas/overlayMarkup.ts';
import { createGlobeMaterial } from './atlas/globeMaterial.ts';
import { createGroundColour } from './atlas/groundColour.ts';
import { albedoTransferOf } from './atlas/albedoTransfer.ts';
import {
  BEHIND_AIR_ORDER, CREDIT_WORD, createPyramidView, GLOBE_DEPTH_PUSH_RADII, shortProduct,
} from './atlas/pyramidView.ts';
import { createProbe } from './atlas/probe.ts';
import type { AtlasGroundGame } from './atlas/groundGame.ts';
import { createSelection } from './atlas/selection.ts';
import { createSurfaceRadius } from './atlas/surfaceRadius.ts';
import { createDojoPaints } from './atlas/dojoPaints.ts';
import { createBodyReaders } from './atlas/bodyReaders.ts';
import { createExposure, BAND_EXPOSURE, EXPOSURE_EASE_PER_FRAME } from './atlas/exposure.ts';
import { createDescentAnchor } from './atlas/descentAnchor.ts';
import { createPlotGeometry } from './atlas/plotGeometry.ts';
import { createZoomLadder } from './atlas/zoomLadder.ts';
import { createOverlayView, REFERENCE_MW, referenceWhSol } from './atlas/overlayView.ts';
import { createAtmosphereView } from './atlas/atmosphereView.ts';
import { createFrameGovernor } from './atlas/frameGovernor.ts';
import { createMount, SCENE_NEAR, STAR_SHELL_M } from './atlas/mount.ts';
import { createAtlasFrame } from './atlas/frame.ts';
import { sharedRenderer } from './world/stage.ts';
import { type WorldVec } from './world/frame.ts';
import {
  gameClock, solAt, MISSION_ANCHOR_T,
} from './world/clock.ts';
import { createCatalogueStars } from './scene/sky.ts';
import { createShaderWarm } from './atlas/shaderWarm.ts';
import { createHexView } from './atlas/hexView.ts';
import { CINEMA_DUR, createCameraRig } from './atlas/cameraRig.ts';
import { createWorldHost, type WorldHostState } from './atlas/worldHost.ts';
import { createViewClock } from './atlas/viewClock.ts';
import { createNavigation } from './atlas/navigation.ts';
import { createPlotMount } from './atlas/plotMount.ts';
import { createFarField } from './atlas/farField.ts';
import { createDescentLoad } from './atlas/descentLoad.ts';
import { createPlotBuild } from './atlas/plotBuild.ts';
import { createPlotSun } from './atlas/plotSun.ts';
import { createFocusRig } from './atlas/focusRig.ts';
import { createBodyScene } from './atlas/bodyScene.ts';
import { createBodySwitch } from './atlas/bodySwitch.ts';
import {
  selectSiteBody, site, siteRegistry, siteUrl,
  zoomFloorOff,
} from './atlas/siteRegistry.ts';
export type { Site } from './atlas/siteRegistry.ts';
import { createFocusGround } from './atlas/focusGround.ts';
import {
  loadState, saveState, urlBody, urlSite,
} from './atlas/viewState.ts';
import type { AtlasState } from './atlas/viewState.ts';
export type { AtlasState, ImageryFloor, Tier } from './atlas/viewState.ts';

declare global {
  interface Window {
    /** DEV only: `followPlotGroundColour`'s owed-retry count, read by
     *  `plot-colour-check.mjs`. A build never defines it. */
    __plotGroundReadbacks?: number;
  }
}

const YAXIS = new THREE.Vector3(0, 1, 0);

// ─────────────────────────────────────────────────────────────────────────────
// Atlas scene — three.js + UI overlay, mounted in the menu's Solar System pane
// ─────────────────────────────────────────────────────────────────────────────

let activeAtlas: { dispose: () => void; adopt: (pane: HTMLElement) => () => void } | null = null;

/**
 * The Solar System view: the one mount, re-hosted into the menu pane.
 *
 * There is one world and two views of it. Opening this view moves the Atlas's
 * own DOM into the pane and gives its rig the camera; closing it puts both back
 * where cruise had them. A page with no mount yet builds one, which is what the
 * Atlas's own dev entries and the editor still do, with the ground game `ground`
 * names; a live mount keeps the one it was built with.
 */
export function renderAtlasPane(host: HTMLElement, ground: AtlasGroundGame): () => void {
  const live = activeAtlas;
  return live ? live.adopt(host) : renderAtlasTab(host, ground);
}

/** The mount's lets that the extracted modules read and write through accessors. */
export interface AtlasLive {
  needsRender: boolean;
  /** Set by anything that moves the camera; the render-on-demand loop reads it. */
  cameraDirty: boolean;
  plot: DtmPlot | null;
  plotField: PlotField | null;
  plotSubs: SubPlot[];
  plotMeshLoading: boolean;
  selectedSub: SubPlot | null;
  /** Which surveyed cell the cursor is over at the plot bands, or none. */
  hoverSub: SubPlot | null;
  disposed: boolean;
}

export function renderAtlasTab(host: HTMLElement, ground: AtlasGroundGame): () => void {
  // Dispose any previous mount (e.g. tab switching)
  activeAtlas?.dispose();
  host.innerHTML = '';

  const root = document.createElement('div');
  root.className = 'atlas-root';
  host.appendChild(root);
  /** Where the mount lives while nothing has adopted it. */
  const bootHost = host;

  const devMode = TEST_SWITCHES && new URLSearchParams(window.location.search).has('dev');
  /** The body the URL opened on, and whether it named one at all. */
  const urlBodyId = urlBody();
  /**
   * The tier, body, quad, cell and plot the Atlas is on.
   *
   * Declared here rather than beside `applyState`: the construction below reads
   * it through `ladderBody()`, and the module-level site registry survives a
   * dispose, so a read that is unreachable on the first mount is reached on the
   * second and throws in the temporal dead zone.
   */
  const state: AtlasState = loadState();

  /**
   * Teardown registry.
   *
   * Every `createX()` in the repo hands back a `dispose`; the Atlas is a
   * large construction that used to free only three objects. So each thing that
   * allocates registers its own release here as it is made, and `dispose()`
   * runs them in reverse. Adding a geometry without an `own()` beside it is
   * then the visible omission.
   */
  const teardown: Array<() => void> = [];
  const own = (fn: () => void): void => { teardown.push(fn); };

  // The Atlas draws through the sim's renderer into a scissored rect of the sim
  // canvas: one GL context, one `renderer.info`, one governor for the machine.
  const renderer = sharedRenderer();

  /**
   * Render on demand.
   *
   * The Atlas ran a 60 fps loop over a static scene: standing still and looking
   * at the terrain redrew an identical image sixty times a second, at the full
   * per-fragment cost of the horizon march. Idle is the COMMON case — a player
   * reading the panel is idle — so idle should be free.
   *
   * Anything that changes the image sets `needsRender`. Continuous states
   * (camera easing, the descent blend, a sun sweep, the flyover) keep setting
   * it while they run. A selection ring still pulses, but at a quarter rate,
   * because a pulse is not worth sixty full redraws a second.
   */
  const live: AtlasLive = {
    needsRender: true,
    cameraDirty: true,
    plot: null,
    plotField: null,
    plotSubs: [],
    plotMeshLoading: false,
    selectedSub: null,
    hoverSub: null,
    disposed: false,
  };

  const frameGovernor = createFrameGovernor({ root, renderer, resize: () => resize() });
  // Tone mapping and colour space belong to the shared renderer; the ones the
  // Atlas wants differently are set and given back per frame by the stage.
  // The sim casts no shadows anywhere (docs/roadmap/05-atmosphere-and-light.md,
  // "Shadows"), so `shadowMap.enabled` stays true for the Atlas's whole visit
  // and the SURFACE regime edge (`updateShadowCascades`) is what actually
  // turns the shadow pass on or off, not this switch.
  const mount = createMount({
    renderer, root, own, frameGovernor, state, exposure: BAND_EXPOSURE.SYSTEM, live,
    // Declared further down this closure: the getters defer each read past
    // construction time instead of tripping its TDZ.
    get focusRig() { return focusRig; },
    get bodyScene() { return bodyScene; },
  });
  const { stage, scene, atlasRoot, worldFrame, resize } = mount;

  // The external-world lets `src/atlas/worldHost.ts` owns: built here, ahead
  // of every factory below that reads or writes one, and handed to
  // `createWorldHost` as `world` rather than snapshotted per factory.
  const worldHostState: WorldHostState = {
    externalWorld: false,
    externalCamWorld: { x: 0, y: 0, z: 0 },
    externalFovDeg: 0,
    externalTileMs: 0,
    cruiseGroundOn: false,
    cruiseGroundHeightM: null,
    worldBodyId: null,
    shipFocusId: null,
    worldEpochJd: null,
    cruiseShipMarker: false,
  };
  const injects = createInjects();

  const viewClockHandle = createViewClock({ injects });
  const { viewClock } = viewClockHandle;

  /**
   * The real sky: the Bright Star Catalogue, in the ecliptic frame the world
   * model propagates in, on a shell inside the far plane. The shell's radius is
   * well under real heliocentric distances at most framings, so occlusion by
   * every body and Sol is draw order (see `createCatalogueStars`), not the
   * depth test. The 800 random points it replaces were noise on a screen that
   * also draws Mars.
   */
  const stars = createCatalogueStars({ radius: STAR_SHELL_M, sizeScale: 1.1 });
  // Ecliptic to scene axes, the one rotation `orrery.eclipticToScene` applies
  // to everything else.
  stars.points.rotation.x = -Math.PI / 2;
  atlasRoot.add(stars.points);
  own(() => stars.dispose());
  void stars.ready.then(() => { live.needsRender = true; });

  const bodyScene = createBodyScene({
    frameGovernor, mount, viewClockHandle, injects, worldHostState, live, own, renderer, state, urlBodyId,
    // Declared further down this closure: the getters defer each read past
    // construction time instead of tripping its TDZ.
    get bodySwitch() { return bodySwitch; },
    get detailGroup() { return detailGroup; },
    get bodyReaders() { return bodyReaders; },
    get focusRig() { return focusRig; },
    get inspector() { return inspector; },
    get shaderWarm() { return shaderWarm; },
    get globeMaterial() { return globeMaterial; },
    get pyramidView() { return pyramidView; },
    get layerPrefs() { return panels.layerPrefs; },
    get siteChips() { return panels.siteChips; },
    get hexViewSeam() { return hexViewSeam; },
    get cameraRig() { return cameraRig; },
    get farField() { return farField; },
    get navigation() { return navigation; },
    get overlayView() { return overlayView; },
  });
  const { showOrrery } = bodyScene;
  const atmosphereView = createAtmosphereView({
    renderer, mount, own, state, bodyScene,
    get bodyReaders() { return bodyReaders; },
    get dojoPaints() { return dojoPaints; },
    get cameraRig() { return cameraRig; },
    get plotMount() { return plotMount; },
    get plotSun() { return plotSun; },
    get solarScrub() { return solarScrub; },
    get surfaceRadius() { return surfaceRadius; },
    get focusRig() { return focusRig; },
    get pyramidView() { return pyramidView; },
    get globeMaterial() { return globeMaterial; },
    get shaderWarm() { return shaderWarm; },
  });

  const surfaceRadius = createSurfaceRadius({
    live, injects, atmosphereView, worldHostState, bodyScene,
    get cameraRig() { return cameraRig; },
    get bodyReaders() { return bodyReaders; },
    get dojoPaints() { return dojoPaints; },
    get pyramidView() { return pyramidView; },
    get _v() { return _v; },
  });

  const bodyReaders = createBodyReaders({
    state, urlBodyId, bodyScene, surfaceRadius, live,
    get focusRig() { return focusRig; },
  });

  const ORIGIN: WorldVec = { x: 0, y: 0, z: 0 };
  const _q = new THREE.Quaternion();
  const _v = new THREE.Vector3();

  const focusGround = createFocusGround({
    state, zoomFloorOff, live, injects,
    plotCamera: () => plotCamera(),
    // Declared further down this closure: the getters defer each read past
    // construction time instead of tripping its TDZ.
    get focusRig() { return focusRig; },
    get solarScrub() { return solarScrub; },
    get pyramidView() { return pyramidView; },
  });

  const focusRig = createFocusRig({
    ORIGIN, _v, own, zoomFloorOff, mount, bodyReaders, surfaceRadius, live, siteRegistry, bodyScene, focusGround,
    injects,
    // Declared further down this closure: the wrap and getter defer each
    // read past construction time instead of tripping its TDZ.
    plotCamera: () => plotCamera(),
    get pyramidView() { return pyramidView; },
  });

  bodyScene.buildMarkers();
  own(() => overlayView.dispose());

  const exposureHandle = createExposure({
    focusRig, bodyReaders, live, stage, state, atmosphereView,
    get navigation() { return navigation; },
  });

  bodyScene.boot();

  // The map overlays: the body's quadrangle outlines and the hex ring, in its
  // own body-fixed frame at its own radius. Registered with the world frame at
  // the body's position and given its rotation each tick, so a lat/lon drawn
  // here stays over the same ground as the body turns.
  const detailGroup = new THREE.Group();
  detailGroup.name = 'body-detail';
  detailGroup.visible = false;
  const detailWorld: WorldVec = { x: 0, y: 0, z: 0 };
  own(worldFrame.register(detailGroup, () => detailWorld));

  const plotMount = createPlotMount({ mount, own, live });
  const { dtmGroup } = plotMount;

  const plotSun = createPlotSun({
    live, bodyReaders, closeness, bodyScene,
    get solarScrub() { return solarScrub; },
    get GRID_STAGES() { return GRID_STAGES; },
    get skyDebug() { return skyDebug; },
    inLocalSky: (row) => cameraRig.inLocalSky(row),
  });

  const descentLoad = createDescentLoad({
    frameGovernor, surfaceRadius, bodyReaders, focusRig, plotSun, site, siteUrl, state, siteRegistry,
    worldHostState, live,
    // Declared further down this closure: the getters defer each read past
    // construction time instead of tripping its TDZ.
    get solarScrub() { return solarScrub; },
    get selection() { return selection; },
    get pyramidView() { return pyramidView; },
    get plotBuild() { return plotBuild; },
    get descentAnchorHandle() { return descentAnchorHandle; },
    get siteGround() { return siteGround; },
    get siteChips() { return panels.siteChips; },
    get overlayView() { return overlayView; },
  });
  const { descentQueue, releaseGroundRing } = descentLoad;

  const eventLogHandle = createEventLog();

  // `label` names the stage a click moves TO, not the one already drawn.
  const GRID_STAGES = [
    { label: 'SHOW PLOT LINES', fill: 0.0, cells: 0.0, parcels: 0.0 },
    { label: 'SHOW SURVEY', fill: 0.0, cells: 0.0, parcels: 0.85 },
    { label: 'HIDE PLOT LINES', fill: 0.55, cells: 0.75, parcels: 0.85 },
  ];
  /**
   * Sols since the mission anchor off the ship's own clock, never the preview.
   * A write stamps this one; `solsSinceAnchor()` (src/atlas/solarScrub.ts) is
   * for drawing only.
   */
  function solsSinceAnchorLive(): number {
    return solAt(gameClock().t) - solAt(MISSION_ANCHOR_T);
  }

  const solarScrub = createSolarScrub({
    YAXIS, focusRig, state, bodyReaders, bodyScene,
    clock: viewClock,
    get dojoPaints() { return dojoPaints; },
    get descentAnchorHandle() { return descentAnchorHandle; },
  });

  // `pyramidView` is declared further down this closure; the wrap defers the
  // call past construction time instead of tripping its TDZ.
  own(() => pyramidView.disposeTileWorker());
  // One region at a time, not an LRU of four. The four-plot cache existed
  // because descending into a plot and backing out re-parsed 33 MB of
  // elevation; there is one whole-file plot now - the walkaround's - and the
  // pyramid's tiles are cached by the tile tree's own LRU by bytes, which is
  // the cache of record for anything the streamer fetches
  // (docs/ATLAS-TERRAIN.md, "The runtime").
  own(() => releasePlot());


  // ── the global pyramid ────────────────────────────────────────────────────
  //
  // The body's own surface, streamed. `atlas/tileTree.ts` decides what is on
  // screen and what is worth fetching, `atlas/tileSource.ts` turns a key into
  // bytes and a `LoadedTile`, and `atlas/tileMesh.ts` turns one of those into
  // geometry on the globe (docs/roadmap/03-terrain-pyramid.md, "The runtime").
  // Everything here is the wiring between the three and the camera.

  const tileGroup = new THREE.Group();
  // Named for whose pyramid it holds, because the instruments read the owner
  // path off it: a Luna tile reported under `mars-pyramid` is a lie in the one
  // place a portability run looks.
  tileGroup.name = 'pyramid';
  tileGroup.visible = false;
  // Registered with the world frame at the focused body's centre and given its
  // rotation each tick, exactly as `detailGroup` is, so a tile stays over the
  // same ground as the body turns and its float32 vertices are camera-relative.
  const tileWorldPos: WorldVec = { x: 0, y: 0, z: 0 };
  own(worldFrame.register(tileGroup, () => tileWorldPos));

  const selection = createSelection({
    own, tileGroup, plotMount, solarScrub, referenceWhSol, closeUp, injects, live,
    // Declared further down this closure: the wraps and getter defer each
    // read past construction time instead of tripping its TDZ.
    plotGroundLocal: (xM, zM, out) => plotGroundLocal(xM, zM, out),
    refreshStructures: () => refreshStructures(),
    syncBuildButton: (cell) => syncBuildButton(cell),
    get pyramidView() { return pyramidView; },
  });

  const pyramidView = createPyramidView({
    mount, frameGovernor, atmosphereView, surfaceRadius, bodyReaders, focusGround, focusRig, descentLoad,
    solarScrub, bodyScene, injects, worldHostState, live,
    get hexViewSeam() { return hexViewSeam; },
    // Declared after this call site, in the moved `dojoPaints` seam: a getter
    // defers the read past construction time instead of tripping its TDZ.
    get dojoPaints() { return dojoPaints; },
    get ladder() { return ladder; },
    get buildPanel() { return panels.buildPanel; },
    _q, _v, own, renderer, root, state, tileGroup, tileWorldPos, urlFlag,
    get cameraRig() { return cameraRig; },
    get farField() { return farField; },
    get globeMaterial() { return globeMaterial; },
    get layerPrefs() { return panels.layerPrefs; },
  });

  const cascades = createCascades({
    atlasRoot, renderer, plotSun, own, live,
    // `cameraRig`, `farField` and `dojoPaints` are declared further down this
    // same closure; a getter defers the read past construction time instead
    // of tripping their TDZ.
    get cameraRig() { return cameraRig; },
    get farField() { return farField; },
    get dojoPaints() { return dojoPaints; },
  });

  const shaderWarm = createShaderWarm({
    renderer, cascades, pyramidView, own, mount, bodyReaders, atmosphereView, bodyScene,
    get dojoPaints() { return dojoPaints; },
    get plotBuild() { return plotBuild; },
  });

  const globeMaterial = createGlobeMaterial({
    pyramidView, atmosphereView, bodyScene, live, own,
    srgbToLinear: (byte) => srgbToLinear(byte),
    get hexViewSeam() { return hexViewSeam; },
  });

  const groundColour = createGroundColour({
    pyramidView, injects, live,
    albedoTransferFor: (id) => albedoTransferOf(bodyReaders.rowById(id)),
    get plotBuild() { return plotBuild; },
    get plotGeometry() { return plotGeometry; },
  });
  const { srgbToLinear } = groundColour;

  const plotBuild = createPlotBuild({
    site, root, state, renderer, frameGovernor, mount, atmosphereView, bodyReaders, solarScrub,
    pyramidView, cascades, globeMaterial, groundColour, descentLoad, plotMount, selection, plotSun,
    injects, bodyScene, live,
    // Declared further down this closure: the getters defer each read past
    // construction time instead of tripping its TDZ.
    get rockTuningPanel() { return panels.rockTuningPanel; },
    get roverDrive() { return panels.roverDrive; },
    get siteGround() { return siteGround; },
    get descentAnchorHandle() { return descentAnchorHandle; },
    get plotGeometry() { return plotGeometry; },
    get overlayView() { return overlayView; },
    get plotControls() { return panels.plotControls; },
    get cameraRig() { return cameraRig; },
    get farField() { return farField; },
    get inspector() { return inspector; },
  });
  const { releasePlot } = plotBuild;

  const descentAnchorHandle = createDescentAnchor({
    site, bodyReaders, focusRig, siteRegistry, live, descentLoad, bodyScene, injects,
    regionXZAt: (lat, lon) => regionXZAt(lat, lon),
    quadFor: (lat, lon) => bodyReaders.quadFor(lat, lon),
    renderOverlay: () => renderOverlay(),
  });

  const ladder = createZoomLadder({
    inSurfaceBand: () => surfaceRegime(),
    onMesh: () => plotCamera(),
    enterSurfaceBand: () => enterSurfaceBand(),
    leaveSurfaceBand: () => leaveSurfaceBand(),
    // The chip reads the band the regime just left for, not the one the
    // in-flight walkaround build is still assembling toward.
    crossed: () => renderOverlay(),
    showOrrery,
    showPlot: (on) => { dtmGroup.visible = on; },
    get legacyPlotFade() { return injects.state.legacyPlotFade; },
    bodyId: () => state.bodyId,
  });

  const plotGeometry = createPlotGeometry({
    mount, pyramidView, solarScrub, bodyReaders, surfaceRadius, bodyScene, injects, live,
    surfaceRegime: () => surfaceRegime(),
    get pick() { return pick; },
  });
  const { regionXZAt, plotGroundLocal } = plotGeometry;


  const siteGround = ground.createSiteGround({
    bodyReaders, plotGeometry, own, solsSinceAnchorLive, live,
    // Declared further down this closure: the getters defer each read past
    // construction time instead of tripping its TDZ.
    get overlayView() { return overlayView; },
    get buildPanel() { return panels.buildPanel; },
  });
  const { bootGround, refreshStructures } = siteGround;

  /** How far the camera has dropped toward the ground, 0 (map) .. 1 (among it). */
  function closeness(): number {
    // 0.10 and 0.02 were plot units; `plotUnitsM()` puts them back in the
    // metre frame `orbit.distance` already carries.
    const u = plotUnitsM();
    const start = injects.state.washFadeInject ? 0.12 : 0.10;
    return THREE.MathUtils.clamp((start * u - orbit.distance) / ((start - 0.02) * u), 0, 1);
  }
  function closeUp(): boolean {
    return closeness() > 0.5;
  }
  let wasCloseUp = false;

  const hexViewSeam = createHexView({
    focusRig, mount, bodyReaders, descentAnchorHandle, surfaceRadius, plotGeometry, bodyScene, injects, live,
    pyramidView, _sunFixed, detailGroup, own, root, state,
    get cameraRig() { return cameraRig; },
    get navigation() { return navigation; },
    get farField() { return farField; },
    get pick() { return pick; },
  });

  const bodySwitch = createBodySwitch({
    state, urlSite, selectSiteBody, bodyReaders, bodyScene, pyramidView, globeMaterial, focusRig, descentLoad,
    descentAnchorHandle, plotBuild, atmosphereView, shaderWarm, hexViewSeam, injects, siteRegistry, live,
    // Declared further down this closure: each getter defers its read past
    // construction time instead of tripping its TDZ.
    get cameraRig() { return cameraRig; },
    get overlayView() { return overlayView; },
    get siteChips() { return panels.siteChips; },
    get inspector() { return inspector; },
  });

  // UI overlay (DOM)
  const overlay = document.createElement('div');
  overlay.className = 'atlas-overlay';
  overlay.innerHTML = overlayMarkup(ground.buildRowsMarkup());
  root.appendChild(overlay);
  injectPlotStyles();

  if (!devMode) {
    for (const id of ['pc-rocks-open', 'atlas-rockctl', 'pc-skin-row', 'pc-credit']) {
      const el = overlay.querySelector(`#${id}`);
      if (el) (el as HTMLElement).style.display = 'none';
    }
  }

  const elBreadcrumb = overlay.querySelector('#atlas-breadcrumb') as HTMLElement;
  const elTier = overlay.querySelector('#atlas-tier') as HTMLElement;
  const elInspector = overlay.querySelector('#atlas-inspector') as HTMLElement;
  const elSampler = overlay.querySelector('#atlas-sampler') as HTMLElement;
  const elSamplerBody = overlay.querySelector('#atlas-sampler-body') as HTMLElement;
  const elBadge = overlay.querySelector('#atlas-badge') as HTMLElement;
  /** The badge's last text, and the clock instant the tick last read it at. */
  let badgeHtml: string | null = null;
  let badgeT = NaN;
  const elSamplerHost = elSampler;
  const elHero = overlay.querySelector('#atlas-hero') as HTMLElement;
  const elHeroCta = overlay.querySelector('#atlas-hero-cta') as HTMLElement;
  overlay.querySelector('#atlas-zoom-out')?.addEventListener('click', () => zoomOut());

  const panels = createPanels({
    overlay, own, root, devMode, state, live, GRID_STAGES, elSampler, elSamplerHost, pyramidView, bodyScene,
    hexViewSeam, atmosphereView, eventLogHandle, selection, bodyReaders, solarScrub, plotSun, descentLoad, injects,
    viewClockHandle, bodySwitch, focusRig, plotBuild, plotGeometry, siteGround, ground,
    // Declared further down this closure: the wraps and getters defer each
    // read past construction time instead of tripping its TDZ.
    zoomIntoBody: (id, source) => zoomIntoBody(id, source),
    renderOverlay: () => renderOverlay(),
    syncTierToAltitude: () => syncTierToAltitude(),
    get cameraRig() { return cameraRig; },
    get overlayView() { return overlayView; },
    get navigation() { return navigation; },
    get inspector() { return inspector; },
    get input() { return input; },
    get pick() { return pick; },
  });
  const { syncBuildButton } = panels.buildPanel;

  const cameraRig = createCameraRig({
    focusRig, mount, bodyReaders, descentAnchorHandle, plotBuild, plotMount, plotGeometry, plotControls: panels.plotControls, roverDrive: panels.roverDrive,
    bodyScene, viewClockHandle, injects, worldHostState, live, SCENE_NEAR, root, site, state,
    get farField() { return farField; },
    get navigation() { return navigation; },
  });
  const { enterSurfaceBand, leaveSurfaceBand, orbit, plotCamera, plotUnitsM, surfaceRegime } = cameraRig;

  const farField = createFarField({
    mount, plotMount, surfaceRadius, plotSun, cameraRig, solarScrub, bodyScene, injects, live,
    renderer, stars, state, ladder, pyramidView, atmosphereView,
  });

  const navigation = createNavigation({
    state, detailGroup, ladder, saveState, siteUrl, mount, focusRig, bodyReaders, cameraRig, descentAnchorHandle,
    descentLoad, exposureHandle, frameGovernor, hexViewSeam, siteChips: panels.siteChips, bodyScene, bodySwitch, siteRegistry, live,
    // Declared further down this closure: the getters defer each read past
    // construction time instead of tripping its TDZ.
    get overlayView() { return overlayView; },
    get inspector() { return inspector; },
  });
  const { syncTierToAltitude, applyState, zoomIntoBody, zoomOut, onAtlasNavigate } = navigation;

  const overlayView = createOverlayView({
    bodyReaders, bodyScene, cameraRig, descentAnchorHandle, focusRig, hexViewSeam, injects, live, mount,
    navigation, plotControls: panels.plotControls, siteGround, siteChips: panels.siteChips, surfaceRadius, viewClockHandle,
    elBadge, elBreadcrumb, elHero, elInspector, elSampler, elSamplerBody, elTier, manifest: panels.manifest, pyramidView, solarScrub, state,
    get addressesReady() { return addressesReady; },
    get ensureCardAddresses() { return ensureCardAddresses; },
    get inspector() { return inspector; },
    get badgeHtml() { return badgeHtml; },
    set badgeHtml(v) { badgeHtml = v; },
  });
  const { renderOverlay } = overlayView;

  // The inspector panel and the honesty overlay (src/atlas/inspector.ts). Every
  // let it reads goes through a handle or `live` so a reassignment here is seen
  // rather than snapshotted at construction; `dojoPaints` is built below this
  // call, which a getter defers.
  const inspector = createInspector({
    solarScrub,
    pyramidView,
    focusRig,
    navigation,
    descentAnchorHandle,
    bodyReaders,
    surfaceRadius,
    eventLogHandle,
    siteGround,
    cameraRig,
    overlayView,
    plotGeometry,
    roverDrive: panels.roverDrive,
    siteRegistry,
    bodyScene,
    descentLoad,
    live,
    REFERENCE_MW,
    CREDIT_WORD,
    state,
    overlay,
    elInspector,
    elHeroCta,
    tileGroup,
    referenceWhSol,
    shortProduct,
    site,
    get dojoPaints() { return dojoPaints; },
    cellBuildings: (cell) => ground.cellBuildings(cell),
    openBuilding: (id) => ground.openBuilding(id),
  });

  const pick = createPick({
    mount,
    hexViewSeam,
    selection,
    navigation,
    descentAnchorHandle,
    focusRig,
    bodyReaders,
    surfaceRadius,
    plotGeometry,
    overlayView,
    cameraRig,
    bodyScene,
    injects,
    live,
    get input() { return input; },
    inspector,
    siteChips: panels.siteChips,
    ORIGIN,
    state,
    tileGroup,
    drawnGroundHit: (origin, dir, far) => pyramidView.drawnGroundHit(origin, dir, far),
    _q,
    _v,
    own,
  });

  const structureView = ground.createStructureView({
    mount, live, plotGeometry, siteGround, own,
    surfaceRegime: () => cameraRig.surfaceRegime(),
    // Declared further down this closure: the getter defers the read past
    // construction time instead of tripping its TDZ.
    get input() { return input; },
  });

  // The sampler card names a cell's MoM seat parcel and reads its claim, both
  // of which need h3-js; entering T3/T4 does not itself await it the way a
  // plot field's own addresses do, so this kicks the load off once and
  // repaints when it lands rather than the card sitting on a "..." forever.
  let addressesReady = false;
  let addressesLoading = false;
  function ensureCardAddresses(): void {
    if (addressesReady || addressesLoading) return;
    addressesLoading = true;
    void ensureAddresses().then(() => {
      addressesReady = true;
      addressesLoading = false;
      renderOverlay();
    });
  }
  const input = createInput({
    focusRig, inspector, cameraRig, own, pick, state, mount, injects, selection, overlayView,
    hexViewSeam, navigation, roverDrive: panels.roverDrive, worldHostState, live, siteChips: panels.siteChips,
    get sliderPreviewing() { return viewClockHandle.state.forecasting || viewClockHandle.state.solScrubbing; },
  });

  // Resize
  const ro = new ResizeObserver(resize);
  ro.observe(root);
  own(() => ro.disconnect());
  resize();

  // Frame
  //
  // Driven by the sim's single rAF through the stage, so the Atlas and the sim
  // can never both be mid-frame on the same renderer.
  const atlasFrame = createAtlasFrame({
    root, state, renderer, mount, injects, viewClockHandle, atmosphereView, plotSun, bodyReaders, focusRig,
    exposureHandle, detailGroup, detailWorld, plotMount, descentLoad, CINEMA_DUR, solarScrub, tileGroup, selection,
    pyramidView, focusGround, cascades, shaderWarm, globeMaterial, groundColour, plotBuild, descentAnchorHandle,
    ladder, closeUp, hexViewSeam, plotControls: panels.plotControls, roverDrive: panels.roverDrive, telemetryPanel: panels.telemetryPanel, cameraRig, farField, overlayView, pick,
    structureView,
    input, frameGovernor, live, bodyScene, worldHostState,
    // Declared further down this closure: the getter defers the read past
    // construction time instead of tripping its TDZ.
    get dojoPaints() { return dojoPaints; },
    get wasCloseUp() { return wasCloseUp; },
    set wasCloseUp(v) { wasCloseUp = v; },
    get badgeT() { return badgeT; },
    set badgeT(v) { badgeT = v; },
  });
  const { tick } = atlasFrame;
  const dojoPaints = createDojoPaints({ atlasRoot, renderer, bodyReaders, atmosphereView, own, live });
  const { skyDebug } = dojoPaints;

  own(() => {
    if (injects.state.colourMistag) {
      injects.state.colourMistag.texture.colorSpace = injects.state.colourMistag.was;
      injects.state.colourMistag = null;
    }
  });

  const worldHost = createWorldHost({
    root, bootHost, state, own,
    world: worldHostState,
    mount, plotMount, injects, focusRig, navigation, farField, plotSun, groundColour, descentLoad,
    bodyScene, bodySwitch, live, cameraRig, pyramidView, globeMaterial, ladder,
    atmosphereView, bodyReaders, surfaceRadius, dojoPaints, input,
  });
  const { adopt } = worldHost;

  // Every member reads the mount's own closure, so the deps are handed over
  // by reference rather than by value: the shared lets cross inside `live`
  // and every handle whole, and nothing here is a snapshot of what the
  // closure held at this line.
  scene.userData['atlasCamera'] = createProbe({
    pyramidView,
    descentLoad,
    injects,
    cameraRig,
    bodyReaders,
    hexViewSeam,
    focusRig,
    descentAnchorHandle,
    shaderWarm,
    bodyScene,
    atmosphereView,
    farField,
    mount,
    dojoPaints,
    plotBuild,
    exposureHandle,
    surfaceRadius,
    plotControls: panels.plotControls,
    navigation,
    plotGeometry,
    roverDrive: panels.roverDrive,
    bodySwitch,
    selection,
    plotSun,
    plotMount,
    frameGovernor,
    overlayView,
    worldHost,
    siteGround,
    stars,
    siteRegistry,
    ladder,
    focusGround,
    viewClockHandle,
    worldHostState,
    live,

    BAND_EXPOSURE,
    BEHIND_AIR_ORDER,
    cascades,
    detailGroup,
    EXPOSURE_EASE_PER_FRAME,
    GLOBE_DEPTH_PUSH_RADII,
    globeMaterial,
    GRID_STAGES,
    input,
    inspector,
    layerPrefs: panels.layerPrefs,
    manifest: panels.manifest,
    overlay,
    pick,
    structureView,
    _q,
    renderer,
    root,
    site,
    siteChips: panels.siteChips,
    solarScrub,
    solsSinceAnchorLive,
    state,
    tileGroup,
    _v,
  });
  own(() => { delete scene.userData['atlasCamera']; });

  stage.setFrame(tick);

  // Initial paint
  solarScrub.syncSolar();
  applyState();

  // The ground the server holds, read once the mount is up. Off the critical
  // path on purpose: nothing here blocks the first frame, and a stage that
  // never answers leaves the caches exactly as they were.
  void bootGround();

  window.addEventListener('atlas:navigate', onAtlasNavigate);
  own(() => window.removeEventListener('atlas:navigate', onAtlasNavigate));

  // What another panel may ask of the ground under this mount, without
  // importing atlas.ts to ask it (src/atlas/groundLink.ts).
  own(ground.createGroundLink({ bodyReaders, solarScrub, navigation, buildPanel: panels.buildPanel }).dispose);

  // Dispose handle: the registry, run in reverse, so nothing is freed before
  // whatever was built on top of it.
  function dispose(): void {
    if (live.disposed) return;
    live.disposed = true;
    // The warm's settle timer and whatever it has on the wire, before the tree
    // and the plot they were fetched for go.
    if (descentLoad.state.warmSettle !== null) { clearTimeout(descentLoad.state.warmSettle); descentLoad.state.warmSettle = null; }
    releaseGroundRing();
    // Each carries a pending timeout, and the tree they clamp is about to go.
    descentQueue.dispose();
    for (let i = teardown.length - 1; i >= 0; i--) {
      try { teardown[i]!(); } catch { /* a failed release must not skip the rest */ }
    }
    teardown.length = 0;
    activeAtlas = null;
  }
  activeAtlas = { dispose, adopt };
  return dispose;
}

